import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    TextField,
    Typography,
    Alert,
    LinearProgress,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as UncheckedIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { PDI, PDIGoal, PDI_STATUS_COLORS, PDI_STATUS_LABELS } from '../../types/collaborator';
import { createPDI, getPDIs, updatePDIGoalStatus } from '../../services/collaborator';

// O backend marca a meta concluída pelo status; mantém compatibilidade com o campo booleano
const isGoalDone = (goal: PDIGoal) => goal.completed === true || goal.status === 'COMPLETED';

const goalDeadline = (goal: PDIGoal) => goal.deadline || goal.due_date;

const PDISection: React.FC = () => {
    const [pdis, setPdis] = useState<PDI[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [goals, setGoals] = useState<Omit<PDIGoal, 'id' | 'completed' | 'completed_at'>[]>([]);
    const [newGoalTitle, setNewGoalTitle] = useState('');
    const [newGoalDescription, setNewGoalDescription] = useState('');
    const [newGoalDueDate, setNewGoalDueDate] = useState('');
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [updatingGoal, setUpdatingGoal] = useState<number | null>(null);

    const fetchPDIs = async () => {
        try {
            setLoading(true);
            const data = await getPDIs();
            setPdis(data);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar PDIs. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPDIs();
    }, []);

    const handleAddGoal = () => {
        if (!newGoalTitle) return;

        setGoals([
            ...goals,
            {
                title: newGoalTitle,
                description: newGoalDescription,
                due_date: newGoalDueDate || undefined
            }
        ]);

        // Reset form
        setNewGoalTitle('');
        setNewGoalDescription('');
        setNewGoalDueDate('');
    };

    const toggleGoal = async (goal: PDIGoal) => {
        try {
            setUpdatingGoal(goal.id);
            const updated = await updatePDIGoalStatus(goal.id, isGoalDone(goal) ? 'PENDING' : 'COMPLETED');
            setPdis((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Erro ao atualizar o objetivo.');
        } finally {
            setUpdatingGoal(null);
        }
    };

    const handleRemoveGoal = (index: number) => {
        setGoals(goals.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title || !description) {
            return;
        }

        try {
            setSubmitError(null);
            await createPDI({
                title,
                description,
                goals
            });
            setOpenDialog(false);
            fetchPDIs();
            // Reset form
            setTitle('');
            setDescription('');
            setGoals([]);
        } catch (err: any) {
            // Mantém o diálogo (e o que foi digitado) com a mensagem do backend
            setSubmitError(err?.response?.data?.error || 'Erro ao criar PDI. Por favor, tente novamente.');
        }
    };

    if (loading) {
        return <LinearProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                    Plano de Desenvolvimento Individual (PDI)
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenDialog(true)}
                >
                    Novo PDI
                </Button>
            </Box>

            {pdis.length === 0 ? (
                <Alert severity="info">
                    Você ainda não tem nenhum PDI cadastrado.
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {pdis.map((pdi) => (
                        <Grid item xs={12} md={6} key={pdi.id}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6">
                                            {pdi.title}
                                        </Typography>
                                        <Chip
                                            label={PDI_STATUS_LABELS[pdi.status] || pdi.status}
                                            color={PDI_STATUS_COLORS[pdi.status] || 'default'}
                                            size="small"
                                        />
                                    </Box>

                                    <Typography color="text.secondary" paragraph>
                                        {pdi.description}
                                    </Typography>

                                    <Typography variant="subtitle2" gutterBottom>
                                        Objetivos: {pdi.goals.filter(isGoalDone).length} de {pdi.goals.length} concluídos
                                    </Typography>
                                    {pdi.goals.length > 0 && (
                                        <LinearProgress variant="determinate" sx={{ mb: 1, height: 6, borderRadius: 3 }}
                                            value={(pdi.goals.filter(isGoalDone).length / pdi.goals.length) * 100} />
                                    )}
                                    <List>
                                        {pdi.goals.map((goal) => (
                                            <ListItem key={goal.id}>
                                                <ListItemIcon>
                                                    {/* Clique alterna entre concluída e pendente */}
                                                    <Tooltip title={isGoalDone(goal) ? 'Marcar como pendente' : 'Marcar como concluída'}>
                                                        <span>
                                                            <IconButton edge="start" disabled={updatingGoal === goal.id}
                                                                aria-label={`${isGoalDone(goal) ? 'Reabrir' : 'Concluir'} objetivo ${goal.title}`}
                                                                onClick={() => toggleGoal(goal)}>
                                                                {isGoalDone(goal) ? (
                                                                    <CheckCircleIcon color="success" />
                                                                ) : (
                                                                    <UncheckedIcon />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </ListItemIcon>
                                                <ListItemText
                                                    primaryTypographyProps={{ sx: isGoalDone(goal) ? { textDecoration: 'line-through', color: 'text.secondary' } : {} }}
                                                    primary={goal.title}
                                                    secondary={
                                                        <>
                                                            {goal.description}
                                                            {goalDeadline(goal) && (
                                                                <Typography component="span" variant="body2">
                                                                    {' '}• Prazo: {new Date(goalDeadline(goal) as string).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Box mt={2}>
                                        <Typography variant="caption" color="text.secondary">
                                            Criado em: {new Date(pdi.created_at).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>Novo PDI</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            {submitError && (
                                <Grid item xs={12}>
                                    <Alert severity="error">{submitError}</Alert>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Título"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descrição"
                                    multiline
                                    rows={4}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Objetivos
                                </Typography>

                                <List>
                                    {goals.map((goal, index) => (
                                        <ListItem
                                            key={index}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={() => handleRemoveGoal(index)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemText
                                                primary={goal.title}
                                                secondary={
                                                    <>
                                                        {goal.description}
                                                        {goal.due_date && (
                                                            <Typography component="span" variant="body2">
                                                                {' '}• Prazo: {goal.due_date}
                                                            </Typography>
                                                        )}
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>

                                <Box sx={{ mt: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Título do Objetivo"
                                                value={newGoalTitle}
                                                onChange={(e) => setNewGoalTitle(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Descrição do Objetivo"
                                                multiline
                                                rows={2}
                                                value={newGoalDescription}
                                                onChange={(e) => setNewGoalDescription(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Data Limite"
                                                type="date"
                                                value={newGoalDueDate}
                                                onChange={(e) => setNewGoalDueDate(e.target.value)}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddGoal}
                                                disabled={!newGoalTitle}
                                            >
                                                Adicionar Objetivo
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!title || !description}
                        >
                            Criar PDI
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default PDISection; 